import Button from 'components/Button';
import BaseModal from 'components/Modals/BaseModal/BaseModal';
import { ModalType } from 'core/useCases/modal/types';
import { FC, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useModalStore } from 'stores/modal';
import {
    SField,
    SHint,
    SLabel,
    SLlmLink,
    SLlmLinks,
    SPromptBox,
    SPromptText,
    SStepTitle,
    STextInput,
    STextarea,
} from '../PromptGeneratorForm/styles';

type FormInputs = {
    job: string;
    sector: string;
    location: string;
    precision?: string;
};

type Props = {
    closeModal(): void;
};

const generatePrompt = (values: FormInputs): string => {
    const precisionLine = values.precision ? `- Précision sur la recherche : ${values.precision}\n` : '';

    return `Tu es un expert en recherche d'emploi et en veille du marché du travail. Je cherche à identifier des opportunités professionnelles ciblées.

            Contexte de ma recherche :
            - Poste recherché : ${values.job}
            - Secteur d'activité : ${values.sector}
            - Localisation : ${values.location}
            ${precisionLine}
            Ta mission : trouve une liste de 20 offres d'emploi récentes (moins d'un mois) correspondant à mon profil. Ces offres doivent provenir d'entreprises dans le secteur concerné. 

            Génère UNIQUEMENT un fichier CSV brut (pas de markdown, pas de balises, pas de bloc de code, pas de mise en forme) avec exactement ces colonnes dans cet ordre :
            Company,Job Title,Place,Salary,Reference,Source,Date of Publication,Description,Job URL

            Règles STRICTES :
            - NE PAS utiliser de markdown : pas de **, pas de __, pas de [], pas de <>
            - Company : nom de l'entreprise qui recrute dans le secteur ${values.sector}
            - Job Title : intitulé précis du poste (ex: Développeur Full Stack Senior, Responsable Marketing Digital, etc.)
            - Place : ville ou région du poste (basé à ${values.location} et alentours)
            - Salary : fourchette salariale estimée (ex: 45-55K€) ou laisser vide
            - Reference : référence de l'offre si existante
            - Source : l'une des valeurs suivantes uniquement : job-board, agency, free-application, my-network, other
            - Date of Publication : date au format DD/MM/YYYY (dans les 30 derniers jours)
            - Description : courte description du poste en une phrase
            - Job URL : URL directe de l'annonce sur le site de l'entreprise ou jobboard 

            Commence directement par la ligne d'en-tête CSV, sans aucun texte avant ou après dans le fichier.
    `;
};

const PromptGeneratorOpportunityForm: FC<Props> = ({ closeModal }) => {
    const { t } = useTranslation();
    const openModal = useModalStore((s) => s.openModal);
    const [generatedPrompt, setGeneratedPrompt] = useState<string | null>(null);

    const {
        handleSubmit,
        register,
        formState: { errors },
    } = useForm<FormInputs>();

    const onSubmit = handleSubmit((values) => {
        setGeneratedPrompt(generatePrompt(values));
    });

    const handleCopy = () => {
        if (!generatedPrompt) return;
        navigator.clipboard.writeText(generatedPrompt).then(() => {
            toast.success(t('prompt-generator.copy-success'));
        });
    };

    const handleOpenImport = () => {
        openModal({
            defaultValues: { pasteMode: true },
            title: t('prompt-generator-opportunity.import-title'),
            type: ModalType.Applications,
        });
    };

    const handleOpenGemini = () => {
        if (!generatedPrompt) return;
        navigator.clipboard.writeText(generatedPrompt).then(() => {
            toast.info(t('prompt-generator.gemini-hint'));
            window.open('https://gemini.google.com/app', '_blank', 'noopener,noreferrer');
        });
    };

    if (generatedPrompt) {
        const encodedPrompt = encodeURIComponent(generatedPrompt);
        const chatGptUrl = `https://chatgpt.com/?q=${encodedPrompt}`;
        const claudeUrl = `https://claude.ai/new?q=${encodedPrompt}`;
        const grokUrl = `https://x.com/i/grok?text=${encodedPrompt}`;

        return (
            <>
                <SStepTitle>{t('prompt-generator.prompt-ready')}</SStepTitle>
                <SHint>{t('prompt-generator-opportunity.prompt-hint')}</SHint>
                <SLlmLinks>
                    <Button onClick={handleCopy} variant="outlined">
                        {t('prompt-generator.copy')}
                    </Button>
                    <SLlmLink href={chatGptUrl} rel="noopener noreferrer" target="_blank">
                        {t('prompt-generator.open-chatgpt')}
                    </SLlmLink>
                    <SLlmLink href={claudeUrl} rel="noopener noreferrer" target="_blank">
                        {t('prompt-generator.open-claude')}
                    </SLlmLink>
                    <SLlmLink as="button" onClick={handleOpenGemini}>
                        {t('prompt-generator.open-gemini')}
                    </SLlmLink>
                    <SLlmLink href={grokUrl} rel="noopener noreferrer" target="_blank">
                        {t('prompt-generator.open-grok')}
                    </SLlmLink>
                </SLlmLinks>
                <SPromptBox>
                    <SPromptText>{generatedPrompt}</SPromptText>
                </SPromptBox>
                <BaseModal.BottomActions>
                    <Button onClick={() => setGeneratedPrompt(null)} variant="text">
                        {t('prompt-generator.back')}
                    </Button>
                    <Button onClick={handleOpenImport}>{t('prompt-generator-opportunity.import-csv')}</Button>
                </BaseModal.BottomActions>
            </>
        );
    }

    return (
        <form onSubmit={onSubmit}>
            <SField>
                <SLabel htmlFor="pgo-job">{t('prompt-generator-opportunity.job')} *</SLabel>
                <STextInput
                    id="pgo-job"
                    placeholder={t('prompt-generator-opportunity.job-placeholder')}
                    {...register('job', { required: true })}
                    hasError={!!errors.job}
                />
            </SField>
            <SField>
                <SLabel htmlFor="pgo-sector">{t('prompt-generator-opportunity.sector')} *</SLabel>
                <STextInput
                    id="pgo-sector"
                    placeholder={t('prompt-generator-opportunity.sector-placeholder')}
                    {...register('sector', { required: true })}
                    hasError={!!errors.sector}
                />
            </SField>
            <SField>
                <SLabel htmlFor="pgo-location">{t('prompt-generator-opportunity.location')} *</SLabel>
                <STextInput
                    id="pgo-location"
                    placeholder={t('prompt-generator-opportunity.location-placeholder')}
                    {...register('location', { required: true })}
                    hasError={!!errors.location}
                />
            </SField>
            <SField>
                <SLabel htmlFor="pgo-precision">{t('prompt-generator-opportunity.precision')}</SLabel>
                <STextarea
                    id="pgo-precision"
                    placeholder={t('prompt-generator-opportunity.precision-placeholder')}
                    rows={3}
                    {...register('precision')}
                />
            </SField>
            <BaseModal.BottomActions>
                <Button onClick={closeModal} variant="text">
                    {t('common.cancel')}
                </Button>
                <Button onClick={handleOpenImport} variant="outlined">
                    {t('prompt-generator-opportunity.skip-to-import')}
                </Button>
                <Button submit>{t('prompt-generator.generate')}</Button>
            </BaseModal.BottomActions>
        </form>
    );
};

export default PromptGeneratorOpportunityForm;
